import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  defaultCohortConfig,
  defaultCohortRow,
  defaultEventCondition,
  defaultExpandEvent,
  defaultFoldEvent,
  defaultLlmContext,
  defaultMeasureFormula,
  defaultMergeEvent,
  defaultPrediction,
  defaultRetention,
  defaultRow,
  defaultSampledCohort,
  defaultStep,
  defaultStepPartitionValue,
} from './defaults';
import {
  canExpandVirtualEvent,
  cohortRowMetaTypes,
  funnelAvailableOrders,
  isEventConditionsCollection,
  isMeasureCohortFilter,
  parseCohortDurationStartDate,
  parseFunnelLookForwardWindow,
  parseRetentionLookForwardWindow,
  removeNegativeStepsByOrder,
  replaceFilter,
  replaceRow,
  resetBuilderAliases,
  resetBuilderSampledCohort,
  resetCompoundMeasure,
  resetErrors,
  resetNamedCohortFields,
  resetNamedMeasureFields,
  selectErrorRow,
  selectEventCondition,
  selectEventConditionsCollection,
  selectEventFilterCondition,
  selectMeasureFilterRow,
  selectRow,
  shouldResetMeasure,
  updateCohortDateRange,
} from './utils';
import {
  CohortSampleContextType,
  IBuilder,
  IBuilderAdditionalSettingsKey,
  IBuilderExpandEvent,
  IBuilderSampledCohortSamplingRate,
  IBuilderSelectedOptions,
  ICohortRow,
  IErrors,
  IFunnelBuilder,
  IMeasureFilterValue,
  IPopulateBuilderPayload,
  IQueryMeasureFormula,
  IRow,
  IRowMeta,
  IStep,
  LookForwardWindowRequirement,
  RowType,
} from './types';
import {
  AttributionKey,
  DateKindKey,
  ExpressionModeKey,
  FilterType,
  FunnelCohortOperatorKey,
  FunnelModeKey,
  FunnelOrderKey,
  GroupbyKey,
  IAnyEventFieldsFragment,
  IAnyTableEventFieldsFragment,
  IBasicEventValueFieldsFragment,
  ICohortCompareFieldsFragment,
  ICohortFieldsFragment,
  ICohortRefPropsFieldsFragment,
  ICohortSampleOperationFieldsFragment,
  IDateRangeFieldsFragment,
  IEventConditionFieldsFragment,
  IFieldFieldsFragment,
  IFilterFieldsFragment,
  ILlmChatMessageFieldsFragment,
  IMeasureFieldsFragment,
  IMeasureFilterFieldsFragment,
  IMeasureFunction,
  IPartitionValueFieldsFragment,
  IPercentileFilterInput,
  IRollingControl,
  ISearchCohortEntityFieldsFragment,
  IVirtualEventFieldsFragment,
  Maybe,
  PathDirectionKey,
  PathModeKey,
  QueryType,
  RetentionCalculationKey,
  RetentionCalculationWindowKey,
  RetentionCohortOperatorKey,
  RetentionViewKey,
  SimpleCohortTypeKey,
  TimeToConvertOperatorKey,
  TimeUnit,
  TimeWindowKey,
} from '../../../utils/apollo/graphqlSchema';
import { MaybeNil } from '../../../utils/interfaces';
import { isNil } from 'lodash';
import {
  MAX_COMPOUND_COHORT_SIZE,
  MAX_EXPAND_EVENTS,
  MAX_FOLD_EVENTS,
  MAX_MERGE_EVENTS,
  ROWS_COUNT_ALLOWS_COMPARE_OR,
} from '../../../config/constants';
import {
  generateCohortRowFromCachedCohort,
  getDefaultCohortValuesFromConfig,
  RETENTION_INTERVAL_VIEW_KEYS,
} from '../../../utils/builder';
import {
  assertCondition,
  assertNotNil,
  byTypeName,
} from '../../../utils/types';
import {
  parseCohortRefToBuilder,
  parseCohortToBuilder,
} from '../../../utils/builder/cohorts';
import {
  parseExpandEvent,
  parseMergeEvent,
} from '../../../utils/hooks/useAnalysisInfoFetch/utils';
import { mergeWithPartial } from '../../../utils/helpers';
import {
  parseCompoundMeasure,
  parseMeasureFormula,
  parseSimpleMeasure,
} from '../../../utils/builder/measures/utils';
import {
  addBreakdownGroupReducer,
  changeBreakdownOrderReducer,
  removeBreakdownGroupReducer,
  resetBreakdownGroupsReducer,
  setBreakdownGroupBinningReducer,
  setBreakdownGroupFieldReducer,
  setBreakdownGroupFilterReducer,
  toggleBreakdownOverallReducer,
} from './breakdown/breakdownReducers';
import { defaultBreakdown } from './breakdown/defaults';
import { ICachedCohortEntity } from '../cachedEntities';
import { populateNextStepReducer } from './nextSteps/populateNextStepReducer';
import {
  removeSignificanceCriteriaVariantReducer,
  setSignificanceBreakdownFieldReducer,
  setSignificanceConfidenceLevelReducer,
  setSignificanceControlGroupReducer,
  setSignificanceErrorsReducer,
  setSignificanceExperimentIdReducer,
  setSignificanceFunnelReportReducer,
  setSignificanceFunnelStepReducer,
  setSignificanceFunnelTimeToConvertMetricReducer,
  setSignificanceLevelReducer,
  setSignificanceMeasureMetricReducer,
  setSignificanceMetricTypeReducer,
  setSignificanceModeReducer,
  setSignificanceQueryReportReducer,
  setSignificanceRetentionReportReducer,
  setSignificanceTestTypeReducer,
  setSignificanceVariantReducer,
} from './significance/significanceReducers';
import { defaultSignificanceBuilderState } from './significance/defaults';
import { defaultDateInput } from './date/defaults';
import { setSelectedDateInput } from './date/dateReducers';
import { removeBinning, setSelectedBinning } from './binning/binningReducers';
import { IDateInput } from './date/types';
import { SUBJECT_EVENT_KEY } from '../../../components/Chart/constants/utils';

// =============================================================

// TODO: plan to group content by type, group footer fields into one config.
// make everything more organized.
export const initialState: IBuilder = {
  cohorts: [],
  defaultLogOps: [],
  defaultMeasureFunctions: [],
  defaultMeasureRelOps: [],
  units: [],
  dataTableMeasures: [defaultRow()],
  direction: PathDirectionKey.Forward,
  steps: [defaultStep(), defaultStep()],
  retention: defaultRetention,
  stepOrders: [],
  paths: [[defaultEventCondition()], [defaultEventCondition()]],
  prediction: defaultPrediction(),
  [RowType.builderCohorts]: [{ ...defaultCohortRow(), isActive: true }],
  builderSampledCohort: defaultSampledCohort(),
  [RowType.predictionCohorts]: [
    { ...defaultCohortRow(), isActive: true },
    { ...defaultCohortRow(), isActive: true },
  ],
  trigger: defaultEventCondition(),
  selectedEvents: [],
  logicOps: [],
  activeSchemaId: '',
  // global time window:
  timeWindow: null,
  alias: '',
  showAlias: false,
  defaultOptions: null,
  selectedOptions: null,
  [RowType.globalCohorts]: [],
  [RowType.breakdownCohorts]: [],
  cachedCohorts: {},
  [RowType.mergeEvents]: [],
  [RowType.expandEvents]: [],
  [RowType.foldEvents]: [],
  formulaSamplingRate: null,
  sourceId: null,
  dateConfig: {
    maxDate: '',
    liveStartDate: '',
  },
  cohortConfig: defaultCohortConfig(),
  resultFilter: null,
  defaultAdditionalSettings: [],
  additionalSettings: [],
  compoundMeasureName: null,
  compoundMeasureId: null,
  breakdown: defaultBreakdown,
  [RowType.measures]: [defaultRow()],
  measureFormula: null,
  comparisonConfig: null,
  significance: defaultSignificanceBuilderState,
  initialConfig: {},
  binning: null,
  dateInput: defaultDateInput,
  builderInitialized: false,
  llmContext: defaultLlmContext,
};

export default createSlice({
  name: 'oldBuilder',
  initialState,
  reducers: {
    setBuilderInitialized: (draft, action: PayloadAction<boolean>) => {
      draft.builderInitialized = action.payload;
    },
    populateConfig: (
      draft,
      action: PayloadAction<IBuilder['initialConfig']>,
    ) => {
      const initialConfig = action.payload;

      return {
        ...draft,
        ...initialConfig,
        initialConfig,
      };
    },
    populateReportData: (
      draft,
      action: PayloadAction<IPopulateBuilderPayload>,
    ) => {
      const payload = action.payload;

      if ('selectedOptions' in payload) {
        return {
          ...draft,
          ...payload,
          selectedOptions: mergeWithPartial<IBuilderSelectedOptions>(
            payload.selectedOptions,
            draft.selectedOptions || ({} as IBuilderSelectedOptions),
          ),
        };
      }

      return {
        ...draft,
        ...payload,
      };
    },
    addNewRowAction: (draft, action: PayloadAction<{ rowMeta: IRowMeta }>) => {
      const { rowMeta } = action.payload;
      const { rowType, rowIndex } = rowMeta;
      const { defaultOptions, selectedOptions } = draft;

      let newRow: IRow | IStep | ICohortRow;
      if (rowType === RowType.steps) {
        assertCondition(
          defaultOptions?.type === QueryType.Funnel &&
            selectedOptions?.type === QueryType.Funnel,
        );

        newRow = defaultStep();

        if (selectedOptions.funnelOrder === FunnelOrderKey.Custom) {
          const defaultFunnelOrder = defaultOptions.funnelCustomOrders[0];
          draft.stepOrders.splice(rowIndex, 0, defaultFunnelOrder.key);
        }
      } else {
        newRow = defaultRow();
      }
      draft[rowType].splice(rowIndex + 1, 0, newRow);

      resetErrors(draft);
    },
    removeRowAction: (draft, action: PayloadAction<IRowMeta>) => {
      const rowMeta = action.payload;
      const { rowType, rowIndex } = rowMeta;

      if (rowType === RowType.steps) {
        const steps = draft[rowType];

        if (steps?.length === 2) {
          steps[rowIndex] = defaultStep();
        } else {
          draft.stepOrders.splice(rowIndex - 1, 1);
          steps.splice(rowIndex, 1);
        }

        return;
      }

      if (rowType === RowType.measures) {
        const currentLength = draft.measures.length;
        if (currentLength === 2) {
          delete draft.alias;
          draft.showAlias = false;
        }

        resetCompoundMeasure(draft);
      }

      draft[rowType].splice(rowIndex, 1);
      resetErrors(draft);
    },
    setRowPropAction: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        row: Partial<ICohortRow>;
      }>,
    ) => {
      const { rowMeta, row } = action.payload;
      const draftRow = selectRow(draft, rowMeta);
      Object.entries(row).forEach(([key, value]) => (draftRow[key] = value));
    },
    setErrorAction: (draft, action: PayloadAction<IErrors>) => {
      draft.errors = {
        ...draft.errors,
        ...action.payload,
      };
    },
    removeErrorAction: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        errorKey: string;
        clearRow?: boolean;
      }>,
    ) => {
      const { rowMeta, errorKey, clearRow } = action.payload;

      const {
        rowType,
        rowIndex,
        eventIndex,
        numberIndex,
        negativeStepRowIndex,
      } = rowMeta;

      if (typeof negativeStepRowIndex === 'number') {
        if (clearRow) {
          delete draft?.errors?.[rowType]?.[rowIndex]?.negativeSteps;
        } else {
          delete draft?.errors?.[rowType]?.[rowIndex]?.negativeSteps?.[
            negativeStepRowIndex
          ];
        }

        return;
      }

      if (rowType === RowType.footer) {
        delete draft?.errors?.[rowType];

        return;
      }

      if (clearRow) {
        delete draft?.errors?.[rowType]?.[rowIndex];

        return;
      }

      if (
        cohortRowMetaTypes.includes(rowType) ||
        isMeasureCohortFilter(rowMeta)
      ) {
        const errorRow = selectErrorRow(draft, rowMeta);

        if (typeof eventIndex === 'number' && eventIndex >= 0) {
          delete errorRow?.[errorKey]?.[eventIndex];
          return;
        }

        if (typeof numberIndex === 'number' && numberIndex >= 0) {
          delete errorRow?.[errorKey]?.[numberIndex];
          return;
        }

        delete errorRow?.[errorKey];
        return;
      }

      if (rowType === RowType.expandEvents || rowType === RowType.foldEvents) {
        delete draft?.errors?.[rowType]?.[rowIndex];
        return;
      }

      if (
        typeof eventIndex === 'number' &&
        eventIndex >= 0 &&
        (rowType === RowType.mergeEvents ||
          rowType === RowType.paths ||
          rowType === RowType.retention)
      ) {
        delete draft?.errors?.[rowType]?.[rowIndex]?.[eventIndex];
        return;
      }

      if (rowType === RowType.global && errorKey === 'timeWindow') {
        delete draft?.errors?.global?.[errorKey];
        return;
      }

      if (rowType === RowType.prediction) {
        delete draft?.errors?.[rowType]?.[errorKey];
        return;
      }

      if (rowType === RowType.trigger) {
        delete draft?.errors?.[rowType];
        return;
      }

      if (typeof eventIndex === 'number' && eventIndex >= 0 && errorKey) {
        delete draft?.errors?.[rowType]?.[rowIndex]?.[errorKey]?.[eventIndex];
        return;
      }

      delete draft?.errors?.[rowType]?.[rowIndex]?.[errorKey];
    },
    addNewEventAction: (draft, action: PayloadAction<IRowMeta>) => {
      const rowMeta = action.payload;
      const eventsCollection = selectEventConditionsCollection(draft, rowMeta);
      eventsCollection.push(defaultEventCondition());
    },
    removeEventRowAction: (draft, action: PayloadAction<IRowMeta>) => {
      const rowMeta = action.payload;
      const { rowType, rowIndex, eventIndex, negativeStepRowIndex } = rowMeta;

      let eventsCollection;
      let eventsCollectionIndex;
      if (negativeStepRowIndex !== undefined && negativeStepRowIndex >= 0) {
        eventsCollectionIndex = negativeStepRowIndex;
        eventsCollection = draft[rowType][rowIndex].negativeSteps;
      } else {
        eventsCollectionIndex = eventIndex;
        eventsCollection = selectEventConditionsCollection(draft, rowMeta);
      }
      eventsCollection.splice(eventsCollectionIndex, 1);
    },
    addEventValuesAction: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        newEvents: (
          | IAnyEventFieldsFragment
          | IVirtualEventFieldsFragment
          | IAnyTableEventFieldsFragment
          | IBasicEventValueFieldsFragment
        )[];
      }>,
    ) => {
      const { rowMeta, newEvents } = action.payload;

      resetNamedMeasureFields(draft, rowMeta);
      resetBuilderAliases(draft, rowMeta);
      resetNamedCohortFields(draft, rowMeta);
      // save selected events obj to local states to prevent defaultEvents changing on search.
      const eventCondition = selectEventCondition(draft, rowMeta);
      if (newEvents.length === 0) {
        eventCondition.events = [];
        eventCondition.field = null;
      } else {
        eventCondition.events = newEvents;
      }

      const virtualEvents = newEvents.filter(byTypeName('VirtualEvent'));
      // clear filter if select virtual event
      if (virtualEvents.length > 0 && eventCondition.filter) {
        delete eventCondition.filter;
      }
    },
    setEventPropertyAction: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        property: MaybeNil<IFieldFieldsFragment>;
      }>,
    ) => {
      const { rowMeta, property } = action.payload;
      const { eventIndex } = rowMeta;

      const draftRow = selectRow(draft, rowMeta);

      if (!draftRow) {
        return;
      }

      resetNamedMeasureFields(draft, rowMeta);
      assertNotNil(eventIndex);
      const currentEvent = draftRow.eventConditions[eventIndex];
      draftRow.eventConditions[eventIndex] = {
        ...currentEvent,
        field: property,
      };
    },
    setFilterAction: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        newFilter: IFilterFieldsFragment | null;
      }>,
    ) => {
      const { rowMeta, newFilter } = action.payload;

      if (shouldResetMeasure(rowMeta)) {
        resetNamedMeasureFields(draft, rowMeta);
      }

      resetBuilderAliases(draft, rowMeta);
      resetNamedCohortFields(draft, rowMeta);

      replaceFilter(draft, rowMeta, newFilter);
    },
    expandNamedFilterAction: (
      draft,
      action: PayloadAction<{ rowMeta: IRowMeta }>,
    ) => {
      const { rowMeta } = action.payload;

      const { filter } = selectEventFilterCondition(draft, rowMeta);

      replaceFilter(draft, rowMeta, {
        __typename: 'BasicFilter',
        type: FilterType.Basic,
        conditions: filter.conditions,
        logicOps: filter.logicOps,
      });
    },
    setMeasureFiltersAction: (
      draft,
      action: PayloadAction<{
        activeMeasureFilters: IMeasureFilterValue[];
        newFilters: IMeasureFilterValue[];
      }>,
    ) => {
      const { activeMeasureFilters, newFilters } = action.payload;

      // we have to make sure that old values are reset or updated
      // and new values are applied too.
      const uniqueValues: Map<
        string,
        { rowMeta: IRowMeta; input: IMeasureFilterFieldsFragment | null }
      > = new Map();

      activeMeasureFilters.forEach(({ rowMeta, measureKey }) => {
        uniqueValues.set(measureKey, { rowMeta, input: null });
      });

      newFilters.forEach(({ rowMeta, measureKey, filter }) => {
        assertNotNil(filter);
        uniqueValues.set(measureKey, { rowMeta, input: filter });
      });

      [...uniqueValues.values()].forEach(({ rowMeta, input }) => {
        const row = selectMeasureFilterRow(draft, rowMeta);

        if (rowMeta.rowType === RowType.global) {
          row.resultFilter = input;
        } else {
          row.measureFilter = input;
        }
      });
    },
    resetMeasureFiltersAction: (
      draft,
      action: PayloadAction<{
        activeMeasureFilters: IMeasureFilterValue[];
      }>,
    ) => {
      const { activeMeasureFilters } = action.payload;

      activeMeasureFilters.forEach(({ rowMeta }) => {
        const row = selectMeasureFilterRow(draft, rowMeta);

        if (rowMeta.rowType === RowType.global) {
          row.resultFilter = null;
        } else {
          row.measureFilter = null;
        }
      });
    },
    convertVirtualToBasicEventAction: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        virtualEventId: string;
      }>,
    ) => {
      const { rowMeta, virtualEventId } = action.payload;

      const eventCondition: MaybeNil<IEventConditionFieldsFragment> =
        selectEventCondition(draft, rowMeta);
      const virtualEvents = eventCondition?.events.filter(
        byTypeName('VirtualEvent'),
      );
      const virtualEventToExpand = virtualEvents?.find(
        (virtualEvent) => virtualEvent.id === virtualEventId,
      );

      if (
        !canExpandVirtualEvent(
          virtualEventToExpand as IVirtualEventFieldsFragment,
          rowMeta,
        )
      ) {
        console.warn(
          'Attempt to expand virtual event with multiple event conditions into row that accepts just one',
          { rowMeta, eventCondition },
        );
        return;
      }

      // unpack the basic events from the virtual event
      const convertedEventConditions:
        | IVirtualEventFieldsFragment['eventsConditions']
        | undefined = virtualEventToExpand?.eventsConditions;

      // update the row in the state depending on if it is a collection or a single event
      if (isEventConditionsCollection(rowMeta)) {
        // collection is indirectly updated via .splice below
        // noinspection JSMismatchedCollectionQueryUpdate
        const collection: IVirtualEventFieldsFragment['eventsConditions'] =
          selectEventConditionsCollection(draft, rowMeta);

        const { eventIndex } = rowMeta;

        assertNotNil(eventIndex);
        if (convertedEventConditions) {
          collection.splice(eventIndex, 1, ...convertedEventConditions);
        }
      } else if (eventCondition && convertedEventConditions?.length === 1) {
        Object.assign(eventCondition, convertedEventConditions[0]);
      }
    },
    restoreInitialConfig: (draft) => {
      return {
        ...initialState,
        ...draft.initialConfig,
        initialConfig: draft.initialConfig,
        builderInitialized: true,
      };
    },
    setBuilderSchemaId: (
      draft,
      action: PayloadAction<{ schemaId: string }>,
    ) => {
      draft.activeSchemaId = action.payload.schemaId;
    },
    // Paths
    builderPathSetDirectionAction: (
      draft,
      action: PayloadAction<{ direction: PathDirectionKey }>,
    ) => {
      const { direction } = action.payload;
      draft.direction = direction;
      // do not allow Any event in the FORWARD/startCondition or BACKWARD/endCondition
      const pathIndex = direction === PathDirectionKey.Forward ? 0 : 1;

      for (const eventCondition of draft.paths[pathIndex]) {
        assertNotNil(eventCondition.events);
        eventCondition.events = eventCondition.events.filter(
          (event) => event.__typename !== 'AnyEvent',
        );
      }
    },
    setCohortRowOperatorValues: (
      draft,
      action: PayloadAction<{ rowMeta: IRowMeta; newValue: number | null }>,
    ) => {
      const { rowMeta, newValue } = action.payload;
      const { numberIndex } = rowMeta;
      assertNotNil(numberIndex);

      resetNamedCohortFields(draft, rowMeta);

      const draftRow: ICohortRow = selectRow(draft, rowMeta);

      draftRow.operatorValues[numberIndex] = newValue;
      resetErrors(draft);
    },

    setCohortRowTimeToConvertOperatorValues: (
      draft,
      action: PayloadAction<{ rowMeta: IRowMeta; newValue: number | null }>,
    ) => {
      const { rowMeta, newValue } = action.payload;
      const { numberIndex } = rowMeta;
      assertNotNil(numberIndex);

      resetNamedCohortFields(draft, rowMeta);

      const draftRow: ICohortRow = selectRow(draft, rowMeta);
      if (draftRow.timeToConvert) {
        draftRow.timeToConvert.operatorValues[numberIndex] = newValue;
      }
      resetErrors(draft);
    },

    setCohortRowTimeToConvertOperator: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        newValue: TimeToConvertOperatorKey;
      }>,
    ) => {
      const { rowMeta, newValue } = action.payload;
      const draftRow: ICohortRow = selectRow(draft, rowMeta);

      if (draftRow.timeToConvert) {
        // operator change will reset operatorValues
        if (draftRow.timeToConvert.operator !== newValue) {
          draftRow.timeToConvert.operatorValues = [null, null];
        }
        draftRow.timeToConvert.operator = newValue;
      }
      resetErrors(draft);
    },

    setCohortRowTimeToConvertTimeUnit: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        newValue: TimeUnit;
      }>,
    ) => {
      const { rowMeta, newValue } = action.payload;
      const draftRow: ICohortRow = selectRow(draft, rowMeta);
      if (draftRow.timeToConvert) {
        draftRow.timeToConvert.timeUnit = newValue;
      }
      resetErrors(draft);
    },

    timeWindowSetAction: (
      draft,
      action: PayloadAction<{ rowMeta: IRowMeta; newValue: string }>,
    ) => {
      const { newValue, rowMeta } = action.payload;
      const { rowType } = rowMeta;

      if (
        cohortRowMetaTypes.includes(rowType) ||
        isMeasureCohortFilter(rowMeta)
      ) {
        const draftCohort = selectRow(draft, rowMeta);
        draftCohort.timeWindow = newValue;
        return;
      }

      if (rowType !== RowType.global) {
        if (rowType === RowType.prediction) {
          draft[rowType].timeWindow = newValue;
        }
      } else {
        draft.globalFrequency.timeWindow = newValue;
      }
    },
    setCohortRowAnalysis: (
      draft,
      action: PayloadAction<{
        analysis: ISearchCohortEntityFieldsFragment | null;
        rowMeta: IRowMeta;
        simpleCohortTypeKey: SimpleCohortTypeKey;
      }>,
    ) => {
      const { analysis, rowMeta, simpleCohortTypeKey } = action.payload;
      const { rowType } = rowMeta;
      const draftRow = selectRow(draft, rowMeta) as ICohortRow;
      resetNamedCohortFields(draft, rowMeta);
      // reset measure id after selecting new analysis
      draftRow.measureId = '';
      draftRow.aliasReplacements = analysis?.pref?.aliasReplacements ?? [];
      draftRow.timeToConvert = null;

      if (!analysis) {
        draftRow.analysisId = '';
        // also reset operator values if analysis is removed
        draftRow.operatorValues = [null, null];
        // KB-6740
        // Special case for Funnel in global cohorts:
        // once analysis is removed, we have to reset cohort row subject
        if (
          rowType === RowType.globalCohorts &&
          simpleCohortTypeKey === SimpleCohortTypeKey.Funnel
        ) {
          const configValues = getDefaultCohortValuesFromConfig(
            draft.cohortConfig,
            simpleCohortTypeKey,
          );
          draftRow.subject = configValues.subject;
        }
        return;
      }

      const { id, query } = analysis;

      draftRow.analysisId = id;
      if (simpleCohortTypeKey === SimpleCohortTypeKey.Funnel) {
        draftRow.operatorValues = [null, null];
        draftRow.operator = FunnelCohortOperatorKey.ConvertedOn;

        // KB-6740
        // Special case for Funnel in global cohorts:
        // once analysis is selected, we have to replace cohort row subject with
        // subject from analysis
        if (
          rowType === RowType.globalCohorts &&
          query.__typename === 'FunnelQuery' &&
          query.subject
        ) {
          draftRow.subject = query.subject.key;
        }
      }

      if (query.__typename === 'FunnelQuery') {
        draftRow.cohortDurationStart = parseCohortDurationStartDate(
          query,
          analysis.start,
        );
        draftRow.lookForwardWindow = parseFunnelLookForwardWindow(query, null);
        if (query?.mode?.key === FunnelModeKey.TimeToConvert) {
          const windowConversionPartition = query.partitions.find(
            (partition) =>
              partition.partition.__typename === 'ConversionWindowPartition',
          );
          draftRow.timeToConvert = {
            operator: TimeToConvertOperatorKey.Equal,
            operatorValues: [null, null],
            timeUnit:
              windowConversionPartition?.conversionWindow?.timeUnit ??
              TimeUnit.Day,
          };
        }
      } else if (query.__typename === 'RetentionQuery') {
        draftRow.cohortDurationStart = parseCohortDurationStartDate(
          query,
          analysis.start,
        );
        draftRow.lookForwardWindow = parseRetentionLookForwardWindow(
          query.retentionUnit,
          null,
        );
      }

      resetErrors(draft);
    },
    builderSetSamplingRate: (
      draft,
      action: PayloadAction<{ newValue: number }>,
    ) => {
      const { newValue } = action.payload;
      draft.formulaSamplingRate = newValue;
    },
    setCohortRowSamplePercentage: (
      draft,
      action: PayloadAction<{
        newValue: number;
        rowMeta: IRowMeta;
      }>,
    ) => {
      const { rowMeta, newValue } = action.payload;
      const draftRow = selectRow(draft, rowMeta);
      draftRow.contextInput.samplePercentage = newValue;
    },
    setCohortRowPercentileFilter: (
      draft,
      action: PayloadAction<{
        input: Maybe<IPercentileFilterInput>;
        rowMeta: IRowMeta;
      }>,
    ) => {
      const { input, rowMeta } = action.payload;
      resetNamedCohortFields(draft, rowMeta);

      const draftRow = selectRow(draft, rowMeta);

      draftRow.percentileFilter = input;
      resetErrors(draft);
    },
    // DND
    swapDataTableMeasure: (
      draft,
      action: PayloadAction<{
        sourceIndex: number;
        targetIndex: number;
      }>,
    ) => {
      const { sourceIndex, targetIndex } = action.payload;

      const newList = [...draft.dataTableMeasures];

      newList.splice(targetIndex, 0, newList.splice(sourceIndex, 1)[0]);

      draft.dataTableMeasures = newList;
      resetErrors(draft);
    },
    swapFunnelStep: (
      draft,
      action: PayloadAction<{
        sourceIndex: number;
        targetIndex: number;
      }>,
    ) => {
      const { sourceIndex, targetIndex } = action.payload;

      const newList = [...draft.steps];

      newList.splice(targetIndex, 0, newList.splice(sourceIndex, 1)[0]);

      // update the step[0].partitionValue to default value after swapping
      newList[0].partitionValue = { ...defaultStepPartitionValue };

      removeNegativeStepsByOrder(draft);
      draft.steps = newList;
      resetErrors(draft);
    },
    swapQueryMeasure: (
      draft,
      action: PayloadAction<{
        sourceIndex: number;
        targetIndex: number;
      }>,
    ) => {
      const { sourceIndex, targetIndex } = action.payload;

      const newList = [...draft.measures];

      newList.splice(targetIndex, 0, newList.splice(sourceIndex, 1)[0]);

      draft.measures = newList;
      resetErrors(draft);
    },
    setCohortRowSubject: (
      draft,
      action: PayloadAction<{
        value: string;
        rowMeta: IRowMeta;
      }>,
    ) => {
      const { value, rowMeta } = action.payload;
      resetNamedCohortFields(draft, rowMeta);

      const draftRow = selectRow(draft, rowMeta);
      draftRow.subject = value;
      resetErrors(draft);
    },
    resetFunnelOrder: (draft) => {
      const { selectedOptions } = draft;

      if (selectedOptions?.type !== QueryType.Funnel) {
        return;
      }

      const funnelOrders = funnelAvailableOrders(draft as IFunnelBuilder);
      const isCurrentOrderValidForNewSubject = funnelOrders.find(
        (fo) => fo.key === selectedOptions.funnelOrder,
      );
      // reset the funnel order to the default value if the current selection is not valid for the funnel orders
      // of the new subject
      if (!isCurrentOrderValidForNewSubject && funnelOrders.length > 0) {
        selectedOptions.funnelOrder = funnelOrders[0].key;
      }
    },
    setSelectedPartitionAction: (
      draft,
      action: PayloadAction<{ value: IPartitionValueFieldsFragment }>,
    ) => {
      const { value } = action.payload;
      const selectedOptions = draft.selectedOptions;

      if (
        selectedOptions?.type === QueryType.Funnel ||
        selectedOptions?.type === QueryType.Path
      ) {
        const { partition } = value;

        if (partition.__typename === 'NonePartition') {
          selectedOptions.partitionValues = [value];
        } else {
          selectedOptions.partitionValues =
            selectedOptions.partitionValues.filter((pv) => {
              return pv.partition.__typename !== 'NonePartition';
            });
          const valueIndex = selectedOptions.partitionValues.findIndex((pv) => {
            return pv.partition.key === value.partition.key;
          });

          if (valueIndex === -1) {
            selectedOptions.partitionValues.push(value);
          } else {
            selectedOptions.partitionValues[valueIndex] = value;
          }
        }
      }
    },
    removeSelectedPartitionAction: (
      draft,
      action: PayloadAction<{
        value: IPartitionValueFieldsFragment;
        noneValue: MaybeNil<IPartitionValueFieldsFragment>;
      }>,
    ) => {
      const { value, noneValue } = action.payload;
      const selectedOptions = draft.selectedOptions;

      if (
        selectedOptions?.type === QueryType.Funnel ||
        selectedOptions?.type === QueryType.Path
      ) {
        selectedOptions.partitionValues =
          selectedOptions.partitionValues.filter((pv) => {
            return pv.partition.key !== value.partition.key;
          });

        if (!isNil(noneValue) && selectedOptions.partitionValues.length === 0) {
          selectedOptions.partitionValues = [noneValue];
        }
      }
    },
    setBuilderTimeWindowAction: (
      draft,
      action: PayloadAction<{ newValue: MaybeNil<number> }>,
    ) => {
      const { newValue } = action.payload;
      draft.timeWindow = newValue;
    },
    setBuilderOrderAction: (
      draft,
      action: PayloadAction<{ newOrder: FunnelOrderKey }>,
    ) => {
      const { newOrder } = action.payload;
      const { defaultOptions, selectedOptions } = draft;

      if (
        defaultOptions?.type !== QueryType.Funnel ||
        selectedOptions?.type !== QueryType.Funnel
      ) {
        return;
      }

      // if selected from other funnel order to CUSTOM order
      if (
        selectedOptions.funnelOrder !== FunnelOrderKey.Custom &&
        newOrder === FunnelOrderKey.Custom
      ) {
        const newStepOrders = [];
        const defaultStepOrder = defaultOptions.funnelCustomOrders[0] ?? null;
        for (let i = 0; i < draft.steps.length - 1; i++) {
          newStepOrders.push(defaultStepOrder?.key);
        }
        draft.stepOrders = newStepOrders;
      } else if (
        selectedOptions.funnelOrder === FunnelOrderKey.Custom &&
        newOrder !== FunnelOrderKey.Custom
      ) {
        draft.stepOrders = [];
      }

      selectedOptions.funnelOrder = newOrder;
      removeNegativeStepsByOrder(draft);
    },
    setBuilderFunnelModeAction: (
      draft,
      action: PayloadAction<{ newMode: FunnelModeKey }>,
    ) => {
      const { newMode } = action.payload;
      const selectedOptions = draft.selectedOptions;

      if (selectedOptions?.type === QueryType.Funnel) {
        selectedOptions.mode = newMode;
      }
    },
    setBuilderPathModeAction: (
      draft,
      action: PayloadAction<{
        newMode: PathModeKey;
      }>,
    ) => {
      const { newMode } = action.payload;
      const selectedOptions = draft.selectedOptions;

      if (selectedOptions?.type === QueryType.Path) {
        selectedOptions.mode = newMode;

        // Top Paths only support direction forward, set it as default
        if (newMode === PathModeKey.TopPaths) {
          draft.direction = PathDirectionKey.Forward;
        }
      }
    },
    setBuilderCustomOrderAction: (
      draft,
      action: PayloadAction<{ rowMeta: IRowMeta; newOrder: FunnelOrderKey }>,
    ) => {
      const { rowMeta, newOrder } = action.payload;
      // step order at rowIndex = N is at index N - 1
      if (rowMeta.rowIndex < 1) {
        throw Error(
          'Invalid rowIndex: rowMeta.rowIndex = (' + rowMeta.rowIndex + ') < 1',
        );
      }
      draft.stepOrders[rowMeta.rowIndex - 1] = newOrder;
      removeNegativeStepsByOrder(draft);
    },
    addBuilderNegativeStep: (
      draft,
      action: PayloadAction<{ stepIndex: number }>,
    ) => {
      const { stepIndex } = action.payload;
      const steps = draft.steps;

      assertCondition(steps[stepIndex]);
      steps[stepIndex].negativeSteps.push(defaultEventCondition());
    },
    setBuilderFunctionAction: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        newFunction: IMeasureFunction;
      }>,
    ) => {
      const { rowMeta, newFunction } = action.payload;

      const draftRow = selectRow(draft, rowMeta);
      draftRow.fn = newFunction;
      resetNamedMeasureFields(draft, rowMeta);
      resetBuilderAliases(draft, rowMeta);
      resetNamedCohortFields(draft, rowMeta);
      if (!newFunction.fieldRequired) {
        if (draftRow?.eventConditions?.[0]?.field) {
          delete draftRow.eventConditions[0].field;
        }
      } else {
        if (draftRow?.eventConditions.length > 1) {
          draftRow.eventConditions = [draftRow.eventConditions[0]];
        }
      }
      if (newFunction.requirePercentile) {
        // XXX KB-6021 - display percentile number as a whole % instead of a fraction
        draftRow.percentile = 90;
      } else {
        delete draftRow.percentile;
      }

      if (newFunction.forceBreakdownOverall) {
        draft.breakdown.overall = true;
      }
    },
    setFunctionPercentileAction: (
      draft,
      action: PayloadAction<{ rowMeta: IRowMeta; newValue: number | null }>,
    ) => {
      const { rowMeta, newValue } = action.payload;
      const draftRow = selectRow(draft, rowMeta);
      draftRow.percentile = newValue;
      resetNamedMeasureFields(draft, rowMeta);
      resetBuilderAliases(draft, rowMeta);
      resetNamedCohortFields(draft, rowMeta);
    },
    addCohortRow: (
      draft,
      action: PayloadAction<{
        rowType: RowType.breakdownCohorts | RowType.globalCohorts;
        limit: number;
      }>,
    ) => {
      const { rowType, limit } = action.payload;

      const cohorts: ICohortRow[] = draft[rowType];

      if (cohorts.length === limit) {
        return;
      }

      const configValues = getDefaultCohortValuesFromConfig(draft.cohortConfig);

      cohorts.push({
        ...defaultCohortRow(),
        ...configValues,
        isActive: true,
      });
    },
    addMeasureCohortFilter: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
      }>,
    ) => {
      const { rowMeta } = action.payload;

      assertCondition(isMeasureCohortFilter(rowMeta));

      const configValues = getDefaultCohortValuesFromConfig(draft.cohortConfig);

      replaceRow(draft, rowMeta, () => ({
        ...defaultCohortRow(),
        ...configValues,
        isActive: true,
      }));
    },
    addSimpleCohort: (draft, action: PayloadAction<IRowMeta>) => {
      const rowMeta = action.payload;
      const { rowType } = rowMeta;

      assertCondition(rowType === RowType.builderCohorts);

      const cohortRows: ICohortRow[] = draft[rowType];

      if (cohortRows.length === MAX_COMPOUND_COHORT_SIZE) {
        return;
      }

      const configValues = getDefaultCohortValuesFromConfig(draft.cohortConfig);

      cohortRows.push({
        ...defaultCohortRow(),
        ...configValues,
        isActive: true,
      });

      resetBuilderSampledCohort(draft);

      const draftBuilderSampledCohort = draft.builderSampledCohort;

      draftBuilderSampledCohort.isCompoundCohort = true;
      draftBuilderSampledCohort.sampleOperation =
        draftBuilderSampledCohort.defaultOperation;
      resetErrors(draft);
    },
    addExpandEventAction: (draft) => {
      const expandEvents = draft[RowType.expandEvents];

      if (expandEvents?.length < MAX_EXPAND_EVENTS) {
        expandEvents.push(defaultExpandEvent());
      }
    },
    addMergeEventAction: (draft) => {
      const mergeEvents = draft[RowType.mergeEvents];

      if (mergeEvents?.length < MAX_MERGE_EVENTS) {
        mergeEvents.push(defaultMergeEvent());
      }
    },
    addFoldEventAction: (draft) => {
      const foldEvents = draft[RowType.foldEvents];

      if (foldEvents?.length < MAX_FOLD_EVENTS) {
        foldEvents.push(defaultFoldEvent());
      }
    },
    removeReroutingEventAction: (
      draft,
      action: PayloadAction<{
        type: RowType.expandEvents | RowType.foldEvents | RowType.mergeEvents;
        index: number;
      }>,
    ) => {
      const { type, index } = action.payload;

      const events = draft[type];

      if (!events?.length) {
        return;
      }

      events.splice(index, 1);
    },
    setMergeEventAlias: (
      draft,
      action: PayloadAction<{
        rowIndex: number;
        newAlias: string;
      }>,
    ) => {
      const { rowIndex, newAlias } = action.payload;
      const mergeEvents = draft[RowType.mergeEvents];

      mergeEvents[rowIndex].name = newAlias;
      mergeEvents[rowIndex].isNameAutoGenerated = false;
    },
    setExpandEventAction: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        expandEvent: {
          field: MaybeNil<IFieldFieldsFragment>;
          event: MaybeNil<IBasicEventValueFieldsFragment>;
        };
      }>,
    ) => {
      const {
        rowMeta: { rowIndex, rowType },
        expandEvent,
      } = action.payload;

      const { event, field } = expandEvent;
      const draftExpandEvent = draft[rowType][rowIndex] as IBuilderExpandEvent;
      draftExpandEvent.field = field;
      if (isNil(draftExpandEvent.eventCondition)) {
        draftExpandEvent.eventCondition = {
          events: [],
          filter: null,
          field: null,
          __typename: 'EventCondition',
        };
      }
      if (!isNil(event)) {
        draftExpandEvent.eventCondition.events = [event];
      } else {
        draftExpandEvent.eventCondition.events = [];
      }
    },
    setCohortRowSimpleTypeKey: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        value: SimpleCohortTypeKey;
      }>,
    ) => {
      const { rowMeta, value } = action.payload;
      const { rowType } = rowMeta;
      // Change of Simple type key will reset cohort row to initial state
      // some values would be taken from config, context input should persist
      const configValues = getDefaultCohortValuesFromConfig(
        draft.cohortConfig,
        value,
      );

      const draftRow = selectRow(draft, rowMeta);

      const cohortRow: ICohortRow = {
        ...defaultCohortRow(),
        ...configValues,
        isActive: true,
        contextInput: draftRow.contextInput,
      };

      replaceRow(draft, rowMeta, () => cohortRow);

      if (rowType === RowType.builderCohorts) {
        resetBuilderSampledCohort(draft);
      }
      resetErrors(draft);
    },
    setCohortRowOperator: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        value: string;
        simpleCohortTypeKey: SimpleCohortTypeKey;
      }>,
    ) => {
      const { rowMeta, value, simpleCohortTypeKey } = action.payload;
      resetNamedCohortFields(draft, rowMeta);

      const draftRow = selectRow(draft, rowMeta);

      // operator change will reset operatorValues
      if (draftRow.operator !== value) {
        draftRow.operatorValues = [null, null];
      }

      draftRow.operator = value;

      if (
        simpleCohortTypeKey === SimpleCohortTypeKey.Retention &&
        value !== RetentionCohortOperatorKey.Returned
      ) {
        draftRow.timeWindow = TimeWindowKey.Overall;
        draftRow.timeWindowValues = [null, null];
      }
      if (
        simpleCohortTypeKey === SimpleCohortTypeKey.Funnel &&
        value.indexOf('DROP_OFF') >= 0
      ) {
        draftRow.operatorValues = [1, null];
      }
      resetErrors(draft);
    },
    setCohortRowReturnedOperator: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        value: string;
      }>,
    ) => {
      const { rowMeta, value } = action.payload;
      resetNamedCohortFields(draft, rowMeta);
      const draftRow = selectRow(draft, rowMeta);

      if (value !== draftRow.returnedOperator) {
        draftRow.operatorValues = [null, null];
      }

      draftRow.returnedOperator = value;
      resetErrors(draft);
    },
    setCohortRowTimeWindow: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        newValue: string;
      }>,
    ) => {
      const { rowMeta, newValue } = action.payload;
      resetNamedCohortFields(draft, rowMeta);

      const draftRow = selectRow(draft, rowMeta);

      // timeWindow change will reset timeWindowValues
      if (draftRow.timeWindow !== newValue) {
        draftRow.timeWindowValues = [null, null];

        // KB-6070: only OVERALL time window supported for rolling control
        if (
          newValue !== TimeWindowKey.Overall &&
          draftRow.measureRollingControl
        ) {
          draftRow.measureRollingControl = null;
        }
      }

      draftRow.timeWindow = newValue;
      resetErrors(draft);
    },
    setCohortRowTimeWindowValues: (
      draft,
      action: PayloadAction<{ rowMeta: IRowMeta; newValue: Maybe<number> }>,
    ) => {
      const { rowMeta, newValue } = action.payload;
      const { numberIndex } = rowMeta;

      assertNotNil(numberIndex);
      resetNamedCohortFields(draft, rowMeta);

      const draftRow = selectRow(draft, rowMeta);
      draftRow.timeWindowValues[numberIndex] = newValue;
      resetErrors(draft);
    },
    removeCohortRow: (draft, action: PayloadAction<IRowMeta>) => {
      const rowMeta = action.payload;
      const { rowIndex, rowType } = rowMeta;

      const cohortRows: ICohortRow[] = draft[rowType];

      const cohortRow = defaultCohortRow();

      if (isMeasureCohortFilter(rowMeta)) {
        replaceRow(draft, rowMeta, () => cohortRow);
        return;
      }

      // remove the row
      draft[rowType].splice(rowIndex, 1);

      // Update cohort sampled values based on amount of cohort rows
      if (rowType === RowType.builderCohorts) {
        resetBuilderSampledCohort(draft);

        const draftBuilderSampledCohort = draft.builderSampledCohort;

        if (cohortRows.length === 1) {
          draftBuilderSampledCohort.isCompoundCohort = false;
          draftBuilderSampledCohort.sampleOperation = null;

          return;
        }

        if (
          draftBuilderSampledCohort.defaultOperation &&
          cohortRows.length !== ROWS_COUNT_ALLOWS_COMPARE_OR
        ) {
          draftBuilderSampledCohort.sampleOperation =
            draftBuilderSampledCohort.defaultOperation;
        }
      }
    },
    addCachedCohort: (
      draft,
      action: PayloadAction<{
        cohort: ICohortFieldsFragment;
      }>,
    ) => {
      const { cohort } = action.payload;

      draft.cachedCohorts[cohort.id] = cohort;
    },
    setAttributionAction: (
      draft,
      action: PayloadAction<{ attributionKey: AttributionKey }>,
    ) => {
      const { attributionKey } = action.payload;
      const selectedOptions = draft.selectedOptions;

      if (selectedOptions?.type === QueryType.Retention) {
        selectedOptions.attribution = attributionKey;
      }
    },
    setCalculationAction: (
      draft,
      action: PayloadAction<{ calculationKey: RetentionCalculationKey }>,
    ) => {
      const { calculationKey } = action.payload;
      const selectedOptions = draft.selectedOptions;

      if (selectedOptions?.type === QueryType.Retention) {
        selectedOptions.calculation = calculationKey;
      }
    },
    setCalculationWindowAction: (
      draft,
      action: PayloadAction<{
        calculationWindowKey: RetentionCalculationWindowKey;
      }>,
    ) => {
      const { calculationWindowKey } = action.payload;
      const selectedOptions = draft.selectedOptions;

      if (selectedOptions?.type === QueryType.Retention) {
        selectedOptions.calculationWindow = calculationWindowKey;
      }
    },
    setCustomTimeBracketAction: (
      draft,
      action: PayloadAction<{ data: number[] } | undefined>,
    ) => {
      if (action.payload) {
        const { data } = action.payload;
        draft.retention.customTimeBrackets = data;
      } else {
        draft.retention.customTimeBrackets = [];
      }
    },
    setCohortRowDatePicker: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        dateInput: MaybeNil<IDateInput>;
      }>,
    ) => {
      const { rowMeta, dateInput } = action.payload;

      resetNamedCohortFields(draft, rowMeta);
      const draftCohort = selectRow(draft, rowMeta) as ICohortRow;

      draftCohort.datePicker = dateInput?.datePicker ?? null;

      const dateRange = !isNil(dateInput)
        ? ({
            __typename: 'DateRange',
            start: dateInput.startDate,
            end: dateInput.endDate,
          } satisfies IDateRangeFieldsFragment)
        : null;
      updateCohortDateRange(draftCohort, dateRange);
      resetErrors(draft);
    },

    setCohortRowDateRange: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        dateRange: MaybeNil<IDateRangeFieldsFragment>;
      }>,
    ) => {
      const { rowMeta, dateRange } = action.payload;
      const draftCohort = selectRow(draft, rowMeta) as ICohortRow;

      // doesn't call resetNamedCohortFields because date range is not
      // part of the named cohort (only datePicker is)

      updateCohortDateRange(draftCohort, dateRange);
      resetErrors(draft);
    },
    setCohortRowDurationStartDate: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        durationStartDate: MaybeNil<string>;
      }>,
    ) => {
      const { rowMeta, durationStartDate } = action.payload;

      resetNamedCohortFields(draft, rowMeta);
      const draftCohort = selectRow(draft, rowMeta) as ICohortRow;
      const { cohortDurationStart } = draftCohort;

      if (cohortDurationStart.required) {
        cohortDurationStart.date = durationStartDate;
      }
      resetErrors(draft);
    },
    setCohortRowLookForwardWindow: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        windowLength: MaybeNil<number>;
      }>,
    ) => {
      const { rowMeta, windowLength } = action.payload;

      resetNamedCohortFields(draft, rowMeta);
      const draftCohort = selectRow(draft, rowMeta) as ICohortRow;
      const { lookForwardWindow } = draftCohort;
      if (
        lookForwardWindow.required === LookForwardWindowRequirement.Required ||
        lookForwardWindow.required ===
          LookForwardWindowRequirement.OnRetentionOperators
      ) {
        lookForwardWindow.windowLength = windowLength;
      }

      resetErrors(draft);
    },
    setGroupbyAction: (
      draft,
      action: PayloadAction<{
        queryType: QueryType.Funnel | QueryType.Path | QueryType.Retention;
        groupbyKey: GroupbyKey;
      }>,
    ) => {
      const { queryType, groupbyKey } = action.payload;
      const selectedOptions = draft.selectedOptions;

      if (selectedOptions?.type === queryType) {
        selectedOptions.groupby = groupbyKey;
      }
    },
    setRetentionViewAction: (
      draft,
      action: PayloadAction<{ retentionViewKey: RetentionViewKey }>,
    ) => {
      const { retentionViewKey } = action.payload;
      const selectedOptions = draft.selectedOptions;

      if (selectedOptions?.type === QueryType.Retention) {
        selectedOptions.retentionView = retentionViewKey;

        if (RETENTION_INTERVAL_VIEW_KEYS.includes(retentionViewKey)) {
          draft.retention.customTimeBrackets = [];
          selectedOptions.calculation = RetentionCalculationKey.Normal;
          selectedOptions.calculationWindow =
            RetentionCalculationWindowKey.StrictCalendarDate;
        }
      }
    },
    resetCohortRow: (draft, action: PayloadAction<IRowMeta>) => {
      const rowMeta = action.payload;

      const configValues = getDefaultCohortValuesFromConfig(draft.cohortConfig);

      // Context input should persist
      const { contextInput, ...cohortRow } = defaultCohortRow();

      const defaultCohort: ICohortRow = {
        ...cohortRow,
        ...configValues,
        isActive: true,
      };

      replaceRow(draft, rowMeta, () => defaultCohort);

      if (
        rowMeta.rowType === RowType.builderCohorts &&
        !draft.builderSampledCohort.isCompoundCohort
      ) {
        resetBuilderSampledCohort(draft);
      }
    },
    setDateKindAction: (
      draft,
      action: PayloadAction<{ dateKindKey: DateKindKey }>,
    ) => {
      const { dateKindKey } = action.payload;
      const selectedOptions = draft.selectedOptions;

      assertCondition(
        selectedOptions?.type === QueryType.Expression ||
          selectedOptions?.type === QueryType.Funnel,
      );

      selectedOptions.dateKind = dateKindKey;
    },
    replaceVirtualEvent: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        virtualEvent: IVirtualEventFieldsFragment;
      }>,
    ) => {
      const { virtualEvent, rowMeta } = action.payload;

      const eventsCollection = selectEventConditionsCollection(draft, rowMeta);
      // keep the first row, and assign the virtual event only
      eventsCollection.splice(1);
      eventsCollection[0] = {
        ...defaultEventCondition(),
        events: [virtualEvent],
      };
    },
    replaceFilterAction: (
      draft,
      action: PayloadAction<{
        rowMeta: IRowMeta;
        filter: IFilterFieldsFragment;
      }>,
    ) => {
      const { filter, rowMeta } = action.payload;
      replaceFilter(draft, rowMeta, filter);
    },
    builderSetRollingControl: (
      draft,
      action: PayloadAction<{
        value: IRollingControl | null;
        rowMeta: IRowMeta;
      }>,
    ) => {
      const { value, rowMeta } = action.payload;

      const draftRow = selectRow(draft, rowMeta);

      resetNamedMeasureFields(draft, rowMeta);
      resetNamedCohortFields(draft, rowMeta);

      draftRow.measureRollingControl = value;
    },
    dataTableMeasureSetNamedMeasure: (
      draft,
      action: PayloadAction<{
        measure: IMeasureFieldsFragment;
        rowMeta: IRowMeta;
      }>,
    ) => {
      const { measure, rowMeta } = action.payload;
      const { queryType } = draft;

      assertCondition(queryType === QueryType.DataTable);

      const draftRow = selectRow(draft, rowMeta);

      if (measure.__typename === 'CompoundMeasure') {
        const compoundMeasure = parseCompoundMeasure(measure, null);

        Object.assign(draftRow, compoundMeasure);
      } else {
        const simpleMeasure = parseSimpleMeasure(measure, null);

        Object.assign(draftRow, simpleMeasure);
      }
    },
    queryMeasureSetNamedMeasure: (
      draft,
      action: PayloadAction<{
        measure: IMeasureFieldsFragment;
        rowMeta?: IRowMeta;
      }>,
    ) => {
      const { measure, rowMeta } = action.payload;
      const { queryType } = draft;

      assertCondition(queryType === QueryType.Expression);
      // This action is only for named measures
      assertNotNil(measure?.version?.versionId);

      if (rowMeta) {
        resetNamedMeasureFields(draft, rowMeta);
      }

      // Compound named measure is a special case.
      // We will populate entire measure to builder
      if (measure.__typename === 'CompoundMeasure') {
        // By design, only a single measure is allowed at this point
        assertCondition(draft.measures.length === 1);

        const { measures, name, version, formula } = measure;

        draft.measures = measures.map((measure) =>
          parseSimpleMeasure(measure, null),
        );

        draft.compoundMeasureName = name;
        draft.compoundMeasureId = version.versionId;

        if (formula) {
          draft.measureFormula = parseMeasureFormula(formula);
        }

        return;
      }
      const simpleMeasure = parseSimpleMeasure(measure, null);

      if (rowMeta) {
        const draftRow = selectRow(draft, rowMeta);

        Object.assign(draftRow, simpleMeasure);
      } else {
        draft.measures = [simpleMeasure];
      }

      const { formula } = measure;

      if (draft.measures.length === 1 && formula) {
        draft.measureFormula = parseMeasureFormula(formula);
      }
    },
    builderSetCohortSampleOperation: (
      draft,
      action: PayloadAction<{
        value: ICohortSampleOperationFieldsFragment;
      }>,
    ) => {
      const { value } = action.payload;
      resetBuilderSampledCohort(draft);
      draft.builderSampledCohort.sampleOperation = value;
    },
    builderSetCohortSamplingRate: (
      draft,
      action: PayloadAction<{
        value: IBuilderSampledCohortSamplingRate;
      }>,
    ) => {
      const { value } = action.payload;
      draft.builderSampledCohort.samplingRate = value;
    },
    builderPopulateCohortAnalysis: (
      draft,
      action: PayloadAction<{
        activeSchemaId: string;
        cohort: ICohortFieldsFragment;
        operation: ICohortSampleOperationFieldsFragment | null;
        compare: ICohortCompareFieldsFragment | null;
        samplePercentage: number | null;
        sampleAbsolute: number | null;
        dateInput: IDateInput;
        lockedDateRange: IDateRangeFieldsFragment | null;
        isPromptVisible: boolean;
        llmChatMessages: ILlmChatMessageFieldsFragment[];
      }>,
    ) => {
      const {
        activeSchemaId,
        cohort,
        operation,
        compare,
        samplePercentage,
        sampleAbsolute,
        dateInput,
        lockedDateRange,
        isPromptVisible,
        llmChatMessages,
      } = action.payload;

      const isCompoundCohort = cohort.__typename === 'CompoundCohort';
      // set active schema ID
      draft.activeSchemaId = activeSchemaId;

      draft.dateInput = dateInput;

      draft.queryType = QueryType.CohortSample;

      draft.llmContext.isPromptVisible = isPromptVisible;
      draft.llmContext.previousMessages = llmChatMessages;

      // gather and populate settings
      const draftBuilderSampledCohort = draft.builderSampledCohort;
      const draftSamplingRate = draftBuilderSampledCohort.samplingRate;

      draftBuilderSampledCohort.isCompoundCohort = isCompoundCohort;
      draftBuilderSampledCohort.sampleOperation = operation;
      draftBuilderSampledCohort.id = cohort?.version?.versionId ?? '';
      draftBuilderSampledCohort.name = cohort?.version?.name ?? '';

      if (samplePercentage !== null) {
        draftSamplingRate.type = CohortSampleContextType.Percentage;
        draftSamplingRate.samplePercentage = samplePercentage;
      } else if (sampleAbsolute !== null) {
        draftSamplingRate.type = CohortSampleContextType.Absolute;
        draftSamplingRate.sampleAbsolute = sampleAbsolute;
      }

      // merge / expand event handling
      if (!isNil(compare)) {
        draft[RowType.expandEvents] =
          compare.expandEvents.map(parseExpandEvent);
        draft[RowType.mergeEvents] = compare.mergeEvents.map(parseMergeEvent);
      } else {
        draft[RowType.expandEvents] = [];
        draft[RowType.mergeEvents] = [];
      }

      const samplePercentageForProps =
        draftSamplingRate.samplePercentage ??
        defaultSampledCohort().samplingRate.samplePercentage;
      assertNotNil(samplePercentageForProps);

      const props: ICohortRefPropsFieldsFragment = {
        __typename: 'CohortRefProps',
        samplePercentage: samplePercentageForProps,
        dateRange: lockedDateRange,
      };

      // populate compound cohort
      if (isCompoundCohort) {
        const { simpleCohorts } = cohort;

        draft[RowType.builderCohorts] = simpleCohorts.map((simpleCohort) => ({
          ...parseCohortRefToBuilder({
            __typename: 'CohortRef',
            cohort: simpleCohort,
            props,
          }),
          isExpanded: true,
        }));

        return;
      }

      // populate simple cohort
      draft[RowType.builderCohorts] = [
        {
          ...parseCohortRefToBuilder({
            __typename: 'CohortRef',
            cohort,
            props,
          }),
          isExpanded: true,
        },
      ];
    },
    populateNamedCohortReference: (
      draft,
      action: PayloadAction<{
        cohort: ICohortFieldsFragment;
        rowMeta: IRowMeta;
      }>,
    ) => {
      const { cohort, rowMeta } = action.payload;
      const { rowType } = rowMeta;

      assertCondition(rowType !== RowType.builderCohorts);
      assertCondition(!isNil(cohort?.version));

      replaceRow(draft, rowMeta, () => ({
        ...defaultCohortRow(),
        ...parseCohortToBuilder(cohort),
      }));
    },
    populateNamedBuilderCohort: (
      draft,
      action: PayloadAction<{
        cohort: ICohortFieldsFragment;
        rowMeta: IRowMeta;
      }>,
    ) => {
      const { cohort, rowMeta } = action.payload;
      const { rowType } = rowMeta;

      assertCondition(rowType === RowType.builderCohorts);
      assertCondition(!isNil(cohort?.version));

      const builderSampledCohort = draft.builderSampledCohort;
      draft.queryType = QueryType.CohortSample;
      draft.activeSchemaId = cohort.schema.id;

      // Case 1: populate simple cohort when isCompoundCohort=true
      if (builderSampledCohort.isCompoundCohort) {
        // In case of compound cohort builder, there should be
        // no Named Compound cohort selection
        assertCondition(cohort.__typename !== 'CompoundCohort');

        resetBuilderSampledCohort(draft);

        replaceRow(draft, rowMeta, () => ({
          ...defaultCohortRow(),
          ...parseCohortToBuilder(cohort),
          isExpanded: true,
        }));

        return;
      }

      // Case 2: populate simple cohort when isCompoundCohort=false
      if (
        !builderSampledCohort.isCompoundCohort &&
        cohort.__typename !== 'CompoundCohort'
      ) {
        // Populate sampled cohort fields
        builderSampledCohort.id = cohort.id;
        builderSampledCohort.name = cohort.version?.name ?? '';

        replaceRow(draft, rowMeta, () => ({
          ...defaultCohortRow(),
          ...parseCohortToBuilder(cohort),
          isExpanded: true,
        }));

        return;
      }

      // Case 3: populate compound cohort when isCompoundCohort=false
      if (
        !builderSampledCohort.isCompoundCohort &&
        cohort.__typename === 'CompoundCohort'
      ) {
        const { schema, simpleCohorts, logicOps, version } = cohort;
        assertNotNil(version);

        const logicOp = logicOps[0].key;

        // We have to find operation by logicOp
        const sampleOperation = builderSampledCohort.operations.find(
          (operation) => operation.logicOp.key === logicOp,
        );

        // Populate sampled cohort fields
        builderSampledCohort.id = version.versionId;
        builderSampledCohort.name = version.name;
        builderSampledCohort.isCompoundCohort = true;
        builderSampledCohort.sampleOperation = sampleOperation || null;

        // Populate active schema ID
        draft.activeSchemaId = schema.id;

        // populate simple cohorts
        draft[RowType.builderCohorts] = simpleCohorts.map((simpleCohort) => ({
          ...defaultCohortRow(),
          ...parseCohortToBuilder(simpleCohort),
          isExpanded: true,
        }));

        return;
      }
    },
    setBuilderAdditionalSettings: (
      draft,
      action: PayloadAction<IBuilderAdditionalSettingsKey[]>,
    ) => {
      draft.additionalSettings = action.payload;
    },
    setSubjectAction: (
      draft,
      action: PayloadAction<{
        queryType:
          | QueryType.Expression
          | QueryType.Path
          | QueryType.Funnel
          | QueryType.Retention
          | QueryType.Significance;
        subjectKey: string | undefined;
      }>,
    ) => {
      const { queryType, subjectKey } = action.payload;
      const selectedOptions = draft.selectedOptions;

      // KBT-1726: Event Subject required null value on Additional Groupby setting
      // when switching from Event Subject to other subjects, we need to set up default groupby value.
      if (
        selectedOptions?.type === QueryType.Funnel &&
        selectedOptions.subject === SUBJECT_EVENT_KEY
      ) {
        const defaultGroupby = draft.funnelConfig?.defaultGroupby;
        assertNotNil(defaultGroupby);
        selectedOptions.groupby = defaultGroupby.key;
      }

      if (selectedOptions?.type === queryType) {
        selectedOptions.subject = subjectKey;
      }
    },
    setTriggerSubjectAction: (
      draft,
      action: PayloadAction<{
        triggerSubjectKey: string;
      }>,
    ) => {
      const { triggerSubjectKey } = action.payload;
      const selectedOptions = draft.selectedOptions;

      if (selectedOptions?.type === QueryType.Expression) {
        selectedOptions.triggerSubject = triggerSubjectKey;
      }
    },
    setExpressionModeAction: (
      draft,
      action: PayloadAction<{
        modeKey: ExpressionModeKey;
      }>,
    ) => {
      const { modeKey } = action.payload;

      const selectedOptions = draft.selectedOptions;

      assertCondition(selectedOptions?.type === QueryType.Expression);

      selectedOptions.mode = modeKey;
      selectedOptions.dateKind =
        modeKey === ExpressionModeKey.Impact
          ? DateKindKey.Trigger
          : DateKindKey.Event;

      // If there was, reset Query comparison config
      if (draft.comparisonConfig) {
        draft.comparisonConfig = null;
      }

      // Reset additional settings when we switch to Query mode
      if (
        modeKey === ExpressionModeKey.Query &&
        draft.additionalSettings.length > 0
      ) {
        draft.additionalSettings = [];
      }
    },
    resetSegments: (draft) => {
      if (!draft[RowType.breakdownCohorts].length) {
        throw new Error('No segments to reset');
      }

      draft[RowType.breakdownCohorts] = [];
    },
    addBreakdownGroup: addBreakdownGroupReducer,
    removeBreakdownGroup: removeBreakdownGroupReducer,
    resetBreakdownGroups: resetBreakdownGroupsReducer,
    changeBreakdownOrder: changeBreakdownOrderReducer,
    toggleBreakdownOverall: toggleBreakdownOverallReducer,
    setBreakdownGroupField: setBreakdownGroupFieldReducer,
    setBreakdownGroupFilter: setBreakdownGroupFilterReducer,
    setBreakdownGroupBinning: setBreakdownGroupBinningReducer,
    addMeasureFormula: (draft) => {
      draft.measureFormula = defaultMeasureFormula;
    },
    removeMeasureFormula: (draft) => {
      draft.measureFormula = null;

      // any change to measure formula will reset resultFilter
      // and named compound measure
      if (!isNil(draft.resultFilter)) {
        draft.resultFilter = null;
      }
      resetCompoundMeasure(draft);
    },
    setMeasureFormulaExpression: (
      draft,
      action: PayloadAction<{
        measureFormula: IQueryMeasureFormula;
        validateOnly?: boolean;
      }>,
    ) => {
      const { measureFormula, validateOnly } = action.payload;

      assertNotNil(draft.measureFormula);
      draft.measureFormula = measureFormula;

      // escape early when validating the value
      if (validateOnly) {
        return;
      }

      // any change to measure formula will reset resultFilter
      if (!isNil(draft.resultFilter)) {
        draft.resultFilter = null;
      }
      // if we have a simple named measure on UI - we will reset it to basic
      if (draft.measures.length === 1) {
        resetNamedMeasureFields(draft, {
          rowType: RowType.measures,
          rowIndex: 0,
        });
      } else {
        // if we have a compound named measure on UI - we will reset it to basic
        resetCompoundMeasure(draft);
      }

      if (measureFormula.isValid && measureFormula.expression?.includes('/')) {
        draft.breakdown.overall = true;
      }
    },
    populateCachedCohort: (
      draft,
      action: PayloadAction<ICachedCohortEntity>,
    ) => {
      const cachedCohort = action.payload;

      const cohortConfig = draft.cohortConfig;

      const builderCohort = generateCohortRowFromCachedCohort(
        cachedCohort,
        cohortConfig,
      );

      draft[RowType.builderCohorts] = [builderCohort];
      draft.activeSchemaId = cachedCohort.schemaId;
      draft.dateInput = {
        startDate: cachedCohort.startDate,
        endDate: cachedCohort.endDate,
        datePicker: {
          __typename: 'DatePickerFixed',
          start: cachedCohort.startDate,
          end: cachedCohort.endDate,
        },
      };
    },
    showStepPartition: (
      draft,
      action: PayloadAction<{
        stepIndex: number;
      }>,
    ) => {
      const { stepIndex } = action.payload;
      const steps = draft.steps;

      assertCondition(steps[stepIndex]);
      steps[stepIndex].showPartition = true;
    },
    setStepPartition: (
      draft,
      action: PayloadAction<{
        stepIndex: number;
        value: IPartitionValueFieldsFragment;
      }>,
    ) => {
      const { stepIndex, value } = action.payload;
      const steps = draft.steps;

      assertCondition(steps[stepIndex]);
      steps[stepIndex].partitionValue = value;
    },
    populateNextStep: populateNextStepReducer,
    setComparisonConfig: (
      draft,
      action: PayloadAction<{
        comparisonConfig: IBuilder['comparisonConfig'];
      }>,
    ) => {
      const { comparisonConfig } = action.payload;

      draft.comparisonConfig = comparisonConfig;
    },
    setSignificanceMode: setSignificanceModeReducer,
    setSignificanceMetricType: setSignificanceMetricTypeReducer,
    setSignificanceMeasureMetric: setSignificanceMeasureMetricReducer,
    setSignificanceFunnelReport: setSignificanceFunnelReportReducer,
    setSignificanceFunnelStep: setSignificanceFunnelStepReducer,
    setSignificanceFunnelTimeToConvertMetric:
      setSignificanceFunnelTimeToConvertMetricReducer,
    setSignificanceQueryReport: setSignificanceQueryReportReducer,
    setSignificanceRetentionReport: setSignificanceRetentionReportReducer,
    setSignificanceTestType: setSignificanceTestTypeReducer,
    setSignificanceLevel: setSignificanceLevelReducer,
    setSignificanceConfidenceLevel: setSignificanceConfidenceLevelReducer,
    removeSignificanceCriteriaVariant: removeSignificanceCriteriaVariantReducer,
    setSignificanceErrors: setSignificanceErrorsReducer,
    setSignificanceBreakdownField: setSignificanceBreakdownFieldReducer,
    setSignificanceExperimentId: setSignificanceExperimentIdReducer,
    setSignificanceControlGroup: setSignificanceControlGroupReducer,
    setSignificanceVariant: setSignificanceVariantReducer,
    setActiveCohortUnit: (
      draft,
      action: PayloadAction<{
        newTimeUnit: TimeUnit;
      }>,
    ) => {
      const { newTimeUnit } = action.payload;
      const selectedOptions = draft.selectedOptions;
      assertCondition(selectedOptions?.type === QueryType.Retention);
      selectedOptions.cohortUnit = newTimeUnit;
    },
    setActiveRetentionUnit: (
      draft,
      action: PayloadAction<{
        newTimeUnit: TimeUnit;
      }>,
    ) => {
      const { newTimeUnit } = action.payload;
      draft.retention.activeRetentionUnit = newTimeUnit;
    },
    setTimeUnit: (
      draft,
      action: PayloadAction<{
        newTimeUnit: TimeUnit;
      }>,
    ) => {
      const { newTimeUnit } = action.payload;
      const selectedOptions = draft.selectedOptions;
      assertCondition(
        selectedOptions?.type === QueryType.Expression ||
          selectedOptions?.type === QueryType.Funnel,
      );

      selectedOptions.timeUnit = newTimeUnit;
    },
    setMissingDatesConfig: (
      draft,
      action: PayloadAction<{
        includeMissingDates: boolean;
      }>,
    ) => {
      const { includeMissingDates } = action.payload;
      assertCondition(
        draft.selectedOptions &&
          draft.selectedOptions.type === QueryType.Expression,
      );
      if (!includeMissingDates) {
        draft.comparisonConfig = null;
      }
      draft.selectedOptions.includeMissingDates = includeMissingDates;
    },

    setSelectedBinning,
    removeBinning,
    setSelectedDateInput,
    resetBuilder: (draft) => {
      return {
        ...initialState,
        activeSchemaId: draft.activeSchemaId,
      };
    },
    builderToggleShowLumosPrompt: (draft, action: PayloadAction<boolean>) => {
      draft.llmContext.isPromptVisible = action.payload;
    },
  },
});
